const workerCode = () => {
  // eslint-disable-next-line no-restricted-globals
  const workerScope = self;

  let accessToken = '';
  let pollingConfig = {};
  let pollingTimer;
  let requestController;
  let requestInFlight = false;
  let running = false;
  let awaitingTokenRefresh = false;
  let authRetryUsed = false;
  let consecutiveFailures = 0;
  let eventSource;

  const clearPollingTimer = () => {
    if (pollingTimer) clearTimeout(pollingTimer);
    pollingTimer = undefined;
  };

  const schedulePoll = (delay = pollingConfig.pollingTime) => {
    clearPollingTimer();
    if (!running || awaitingTokenRefresh || !pollingConfig.srcUrl) return;

    const scheduledDelay = Math.max(0, Number(delay) || 0);
    pollingTimer = setTimeout(runPoll, scheduledDelay);
    workerScope.postMessage({ eventType: 'polling-scheduled', pollingTime: scheduledDelay });
  };

  const requestTokenRefresh = () => {
    awaitingTokenRefresh = true;
    authRetryUsed = true;
    clearPollingTimer();
    workerScope.postMessage({ eventType: 'auth-refresh-required', accessToken });
  };

  const runPoll = async () => {
    if (!running || awaitingTokenRefresh || requestInFlight || !pollingConfig.srcUrl) return;

    requestInFlight = true;
    let nextDelay = pollingConfig.pollingTime;
    let shouldScheduleNext = true;
    requestController =
      typeof AbortController === 'function' ? new AbortController() : undefined;
    const requestTimeout = setTimeout(
      () => requestController && requestController.abort(),
      Math.min(30000, Math.max(5000, Number(pollingConfig.pollingTime) || 0)),
    );

    try {
      const response = await fetch(pollingConfig.srcUrl, {
        method: 'GET',
        ...(requestController ? { signal: requestController.signal } : {}),
        mode: 'cors',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...(pollingConfig.appName ? { 'x-app-name': pollingConfig.appName } : {}),
          ...(pollingConfig.appId ? { 'x-app-id': pollingConfig.appId } : {}),
        },
      });

      if (response.status === 401 || response.status === 403) {
        if (!authRetryUsed) {
          shouldScheduleNext = false;
          requestTokenRefresh();
        } else {
          workerScope.postMessage({
            eventType: 'auth-rejected',
            status: response.status,
            message: 'Authentication failed after one refresh attempt',
          });
        }
        return;
      }

      if (!response.ok) {
        throw new Error(`Assigned-booking polling failed with status ${response.status}`);
      }

      const result = await response.json();
      consecutiveFailures = 0;
      authRetryUsed = false;
      workerScope.postMessage(result);
    } catch (error) {
      if (!running) return;
      consecutiveFailures += 1;
      nextDelay = Math.min(
        pollingConfig.maxPollingTime || pollingConfig.pollingTime * 10,
        pollingConfig.pollingTime * 2 ** Math.min(consecutiveFailures, 4),
      );
      workerScope.postMessage({
        eventType: 'polling-error',
        message: error && error.message ? error.message : 'Polling request failed',
      });
    } finally {
      clearTimeout(requestTimeout);
      requestController = undefined;
      requestInFlight = false;
      if (shouldScheduleNext) schedulePoll(nextDelay);
    }
  };

  const updateToken = (nextAccessToken) => {
    if (!nextAccessToken || !running) return;
    accessToken = nextAccessToken;
    awaitingTokenRefresh = false;
    schedulePoll(0);
  };

  const stopPolling = () => {
    running = false;
    awaitingTokenRefresh = false;
    clearPollingTimer();
    if (requestController) requestController.abort();
  };

  workerScope.onmessage = (event) => {
    const data = event.data || {};

    if (data.eventType === 'update-token' || data.eventType === 'auth-token-updated') {
      updateToken(data.accessToken);
      return;
    }
    if (data.eventType === 'stop-polling' || data.eventType === 'STOP') {
      stopPolling();
      return;
    }
    if (data.eventType === 'sse') {
      if (eventSource) eventSource.close();
      eventSource = new EventSource(data.srcUrl);
      eventSource.onmessage = (message) => workerScope.postMessage(JSON.parse(message.data));
      return;
    }

    if (!data.srcUrl || !data.accessToken || !Number(data.pollingTime)) {
      workerScope.postMessage({
        eventType: 'polling-error',
        message: 'Polling requires srcUrl, accessToken and pollingTime',
      });
      return;
    }

    stopPolling();
    pollingConfig = {
      srcUrl: data.srcUrl,
      pollingTime: Number(data.pollingTime),
      maxPollingTime: Number(data.maxPollingTime) || Number(data.pollingTime) * 10,
      appName: data.appName,
      appId: data.appId,
    };
    accessToken = data.accessToken;
    running = true;
    authRetryUsed = false;
    consecutiveFailures = 0;

    workerScope.postMessage({
      eventType: 'polling-status',
      status: 'started',
      srcUrl: pollingConfig.srcUrl,
      pollingTime: pollingConfig.pollingTime,
    });
    schedulePoll(0);
  };
};

let code = workerCode.toString();
code = code.substring(code.indexOf('{') + 1, code.lastIndexOf('}'));

const blob = new Blob([code], { type: 'application/javascript' });
const workerScript = URL.createObjectURL(blob);

export default workerScript;
